/**
 * Enrollment Data API Service
 * Handles enrollment data from imported Django models
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export interface EnrollmentOverview {
  overview: {
    academic_year: number;
    total_records: number;
    total_schools: number;
    total_male: number;
    total_female: number;
    total_students: number;
    gender_parity_index: number | null;
    female_percentage: number;
  };
  age_distribution: Array<{
    age: number;
    male: number;
    female: number;
    total: number;
  }>;
  district_breakdown: Array<{
    district_name: string;
    district_code: string;
    region_name: string;
    total_schools: number;
    total_male: number;
    total_female: number;
    total_students: number;
    female_percentage: number;
  }>;
}

export interface SchoolEnrollmentDetails {
  academic_year: number;
  schools: Array<{
    school_id: number;
    school_name: string;
    emis_id: string;
    district: string;
    region: string;
    summary: {
      total_male: number;
      total_female: number;
      total_students: number;
      female_percentage: number;
      gender_parity_index: number | null;
    };
    age_breakdown: Array<{
      age: number;
      class_name: string;
      male_count: number;
      female_count: number;
      total_count: number;
      female_percentage: number;
      gender_parity_index: number | null;
    }>;
    class_summary: Array<{
      class_name: string;
      male: number;
      female: number;
      total: number;
      female_percentage: number;
    }>;
  }>;
  total_schools: number;
}

export interface EnrollmentChartData {
  chart_type: string;
  data: Array<{
    [key: string]: any;
  }>;
}

export const getEnrollmentOverview = async (academicYear?: number): Promise<EnrollmentOverview> => {
  const url = new URL(`${API_BASE_URL}/sis-integration/enrollment-overview/`);
  if (academicYear) {
    url.searchParams.append('academic_year', academicYear.toString());
  }

  const response = await fetch(url.toString());
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const getSchoolEnrollmentDetails = async (schoolId?: number, academicYear?: number): Promise<SchoolEnrollmentDetails> => {
  const baseUrl = schoolId 
    ? `${API_BASE_URL}/sis-integration/school-enrollment/${schoolId}/`
    : `${API_BASE_URL}/sis-integration/school-enrollment/`;
  
  const url = new URL(baseUrl);
  if (academicYear) {
    url.searchParams.append('academic_year', academicYear.toString());
  }

  const response = await fetch(url.toString());
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const getEnrollmentChartData = async (chartType: string, academicYear?: number): Promise<EnrollmentChartData> => {
  const url = new URL(`${API_BASE_URL}/sis-integration/enrollment-chart-data/`);
  url.searchParams.append('type', chartType);
  if (academicYear) {
    url.searchParams.append('academic_year', academicYear.toString());
  }

  const response = await fetch(url.toString());
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

export const exportEnrollmentExcel = async (district?: string, academicYear?: number): Promise<Blob> => {
  const url = new URL(`${API_BASE_URL}/sis-integration/export-enrollment-excel/`);
  if (district) {
    url.searchParams.append('district', district);
  }
  if (academicYear) {
    url.searchParams.append('academic_year', academicYear.toString());
  }

  const response = await fetch(url.toString());
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.blob();
};